import React from 'react';
import i18next from 'i18next';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import jwt from 'jsonwebtoken';
import Button from '../Buttons/Button';
import { ReactComponent as AlertTriangle } from '../../assets/images/svg/alert-triangle.svg';
import themeColors from '../../assets/styles/themeColors';
import {
  mediaBelow,
  mediaBetween,
  pxToRem,
  pxToRemMd,
} from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import { useGetIsImpersonating } from '../../util/hooks/useGetIsImpersonating';
import Section from '../Section/Section';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { EMAIL } from '../../constants/sessionStorage';

const AssistedRegistrationBannerContainer = styled.div`
  position: -webkit-sticky;
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${pxToRem(20)} ${pxToRem(46)};
  gap: ${pxToRem(350)};
  background: ${themeColors.colorBackgroundSecondaryLight};
  border-top: 4px solid ${themeColors.colorRed};
  border-bottom: 1px solid ${themeColors.colorRed};
  border-left: 1px solid ${themeColors.colorRed};
  border-right: 1px solid ${themeColors.colorRed};
  ${mediaBetween(variables.breakpoints.bpMd, variables.breakpoints.bpXl)} {
    gap: ${pxToRemMd(32)};
    flex-direction: column;
  }
  ${mediaBelow(variables.breakpoints.bpMd)} {
    gap: ${pxToRemMd(10)};
    flex-direction: column;
  } ;
`;

const AssistedRegistrationBannerTitle = styled.p`
  color: ${themeColors.colorTextSecondary};
  line-height: 1;
  font-weight: 600;
  font-size: ${pxToRem(24)};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: ${pxToRemMd(20)};
  }
`;

const AssestedRegistrationBannerLogout = styled(Button)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 13px;
  gap: 8px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin-left: 0;
    margin-top: ${pxToRemMd(12)};
    max-width: ${pxToRemMd(320)};
    min-width: 0;
    width: 100%;
    padding: ${pxToRemMd(4)} ${pxToRemMd(22)};
    font-size: ${pxToRemMd(16)};
    border: 1px solid ${themeColors.colorTextSecondary};
  }
`;

const AssistedRegistrationBannerSaveDraftContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${pxToRem(16)};
`;
const LogoutTitleWrap = styled.div`
  display: flex;
  flex-direction: row;
`;

const AssistedRegistrationEmailContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 10px;
  align-items: center;
  ${mediaBelow(variables.breakpoints.bpSm)} {
    flex-direction: column;
  }
`;

const AssistedRegistrationEmailBox = styled.div`
  background-color: ${themeColors.colorTextSecondary};
  border-radius: ${variables.borderRadius.borderRadius};
  color: ${themeColors.colorWhite};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
`;

const AssistedRegistrationEmail = styled.div`
  text-align: center;
  margin: auto;
`;

const ImpersonateBannerContent = styled(Section)`
  display: flex;
  align-items: center;
  flex-direction: row;
  ${mediaBelow(variables.breakpoints.bpSm)} {
    flex-direction: column;
  }
`;
const ImpersonateBannerIcon = styled(AlertTriangle)`
  width: ${pxToRem(20)};
  height: ${pxToRem(20)};
  color: ${themeColors.colorRed};
  margin-right: ${pxToRem(8)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    width: ${pxToRemMd(20)};
    height: ${pxToRemMd(20)};
    margin-right: ${pxToRemMd(16)};
  }
`;

const ImpersonateBannerTypography = styled.p`
  color: ${themeColors.colorRed};
  font-weight: 400;
  letter-spacing: 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    text-align: center;
  }
`;

const AssistedRegistrationCautionWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AssistedRegistrationBannerWrap = styled.div`
  padding: 0 1.5rem;
  margin: 0 auto;
  max-width: 65.75rem;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items:center;
  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;
  }
}
`;

const AssistedRegistrationBanner = ({
  formId,
  setEventType,
  eventType,
  setSaveAndCloseModalOpen,
}) => {
  const { isSalesAssisted } = useGetIsImpersonating();
  const JwtToken = retrieveFromSessionStorage('JwtToken');
  const user = jwt.decode(JwtToken);
  const email = retrieveFromSessionStorage(EMAIL);

  if (!isSalesAssisted) {
    return null;
  }

  return (
    <AssistedRegistrationBannerContainer>
      <AssistedRegistrationBannerWrap>
        <AssistedRegistrationCautionWrap>
          <AssistedRegistrationEmailContainer>
            <AssistedRegistrationBannerTitle>
              {i18next.t('assistedBanner.title')}
            </AssistedRegistrationBannerTitle>
            <AssistedRegistrationEmailBox>
              <AssistedRegistrationEmail>
                {user?.Email ? user?.Email : email}
              </AssistedRegistrationEmail>
            </AssistedRegistrationEmailBox>
          </AssistedRegistrationEmailContainer>
          <ImpersonateBannerContent>
            <ImpersonateBannerIcon />
            <ImpersonateBannerTypography>
              {i18next.t('impersonate.caution')}
            </ImpersonateBannerTypography>
          </ImpersonateBannerContent>
        </AssistedRegistrationCautionWrap>
        <AssistedRegistrationBannerSaveDraftContainer>
          <AssestedRegistrationBannerLogout
            size="lg"
            type="submit"
            form={formId}
            id="test2"
            title={
              <LogoutTitleWrap>
                {i18next.t('assistedBanner.logout')}
              </LogoutTitleWrap>
            }
            onClick={() =>
              eventType === 'NoForm'
                ? setSaveAndCloseModalOpen(true)
                : setEventType('Banner')
            }
          />
        </AssistedRegistrationBannerSaveDraftContainer>
      </AssistedRegistrationBannerWrap>
    </AssistedRegistrationBannerContainer>
  );
};

AssistedRegistrationBanner.propTypes = {
  formId: PropTypes.string,
  setEventType: PropTypes.func,
  eventType: PropTypes.string,
  setSaveAndCloseModalOpen: PropTypes.func,
};

export default AssistedRegistrationBanner;
